package com.example.positionahead.data

import com.example.positionahead.model.AssetClass
import com.example.positionahead.model.FinancialGoal
import com.example.positionahead.model.PortfolioObjective
import com.example.positionahead.model.Position
import com.example.positionahead.model.PositionLot
import com.example.positionahead.model.RiskTolerance
import com.example.positionahead.model.SavedPortfolio
import com.example.positionahead.model.WatchlistItem
import com.example.positionahead.model.deepCopy
import com.example.positionahead.model.positionWithSingleLot
import com.example.positionahead.model.withAddedLot
import com.example.positionahead.model.withSoldLot
import com.example.positionahead.model.withUpdatedLot
import com.example.positionahead.model.withoutLot
import com.russhwolf.settings.Settings
import com.russhwolf.settings.set
import kotlin.random.Random
import kotlinx.serialization.Serializable
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json

class PortfolioRepository(
    private val settings: Settings,
    private val json: Json = Json { ignoreUnknownKeys = true },
) {
    private var cache: List<SavedPortfolio>? = null
    private var activeIdCache: String? = null

    fun getPortfolios(): List<SavedPortfolio> {
        cache?.let { return it }
        migrateLegacyDataIfNeeded()
        val raw = settings.getStringOrNull(KEY_PORTFOLIOS) ?: return defaultPortfolios().also { cache = it }
        val portfolios = runCatching {
            json.decodeFromString<List<SavedPortfolio>>(raw)
        }.getOrElse { defaultPortfolios() }
        cache = portfolios.ifEmpty { defaultPortfolios() }
        return cache!!
    }

    fun getActivePortfolioId(): String {
        activeIdCache?.let { return it }
        migrateLegacyDataIfNeeded()
        val id = settings.getStringOrNull(KEY_ACTIVE_PORTFOLIO) ?: DEFAULT_PORTFOLIO_ID
        activeIdCache = id
        return id
    }

    fun getActivePortfolio(): SavedPortfolio {
        val portfolios = getPortfolios()
        return portfolios.find { it.id == getActivePortfolioId() }
            ?: portfolios.first()
    }

    fun setActivePortfolio(id: String) {
        if (getPortfolios().none { it.id == id }) return
        activeIdCache = id
        settings[KEY_ACTIVE_PORTFOLIO] = id
    }

    fun createPortfolio(
        name: String,
        copyFromPortfolioId: String? = null,
        colorHex: String = com.example.positionahead.model.AccountColors.DEFAULT,
        startDate: String? = null,
        notes: String = "",
        isTheoretical: Boolean = false,
    ): SavedPortfolio {
        val source = copyFromPortfolioId?.let { id ->
            getPortfolios().find { it.id == id }
        }
        val newId = Random.nextLong().toString(16)
        val portfolio = SavedPortfolio(
            id = newId,
            name = name.trim().ifBlank { "Account ${getPortfolios().size + 1}" },
            colorHex = colorHex,
            startDate = startDate,
            notes = notes,
            isTheoretical = isTheoretical,
            positions = source?.positions?.map { position ->
                position.deepCopy(newId = Random.nextLong().toString(16))
            } ?: emptyList(),
            goal = source?.goal ?: FinancialGoal(),
        )
        savePortfolios(getPortfolios() + portfolio)
        setActivePortfolio(newId)
        return portfolio
    }

    fun createTheoreticalPortfolio(name: String): SavedPortfolio =
        createPortfolio(name = name, isTheoretical = true)

    fun updatePortfolioDetails(
        id: String,
        name: String,
        colorHex: String,
        startDate: String?,
        notes: String,
    ) {
        updatePortfolio(id) {
            it.copy(
                name = name.trim().ifBlank { it.name },
                colorHex = colorHex,
                startDate = startDate,
                notes = notes,
            )
        }
    }

    fun renamePortfolio(id: String, name: String) {
        updatePortfolio(id) { it.copy(name = name.trim()) }
    }

    fun deletePortfolio(id: String) {
        val portfolios = getPortfolios()
        if (portfolios.size <= 1) return
        val remaining = portfolios.filterNot { it.id == id }
        savePortfolios(remaining)
        if (getActivePortfolioId() == id) {
            setActivePortfolio(remaining.first().id)
        }
    }

    fun getPositions(): List<Position> = getActivePortfolio().positions

    fun getGoal(): FinancialGoal = getActivePortfolio().goal

    fun savePositions(positions: List<Position>) {
        updateActive { it.copy(positions = positions) }
    }

    fun setPortfolioPositions(portfolioId: String, positions: List<Position>) {
        updatePortfolio(portfolioId) {
            it.copy(
                positions = positions.map { position ->
                    position.deepCopy(newId = Random.nextLong().toString(16))
                },
            )
        }
    }

    fun saveGoal(goal: FinancialGoal) {
        updateActive { it.copy(goal = goal) }
    }

    fun addPosition(position: Position, portfolioId: String? = null) {
        if (portfolioId == null || portfolioId == getActivePortfolioId()) {
            savePositions(getPositions() + position)
        } else {
            updatePortfolio(portfolioId) { it.copy(positions = it.positions + position) }
        }
    }

    fun updatePosition(position: Position, portfolioId: String? = null) {
        val targetId = portfolioId ?: getActivePortfolioId()
        updatePortfolio(targetId) { p ->
            p.copy(positions = p.positions.map { if (it.id == position.id) position else it })
        }
    }

    fun removePosition(id: String, portfolioId: String? = null) {
        val targetId = portfolioId ?: getActivePortfolioId()
        updatePortfolio(targetId) { p ->
            p.copy(positions = p.positions.filterNot { it.id == id })
        }
    }

    fun addLot(
        positionId: String,
        shares: Double,
        costBasisPerShare: Double,
        purchaseDate: String? = null,
        note: String? = null,
        portfolioId: String? = null,
    ) {
        val lot = PositionLot(
            id = newLotId(),
            shares = shares,
            costBasisPerShare = costBasisPerShare,
            purchaseDate = purchaseDate,
            note = note,
        )
        val targetId = portfolioId ?: getActivePortfolioId()
        val portfolio = getPortfolios().find { it.id == targetId } ?: return
        val position = portfolio.positions.find { it.id == positionId } ?: return
        updatePosition(position.withAddedLot(lot), portfolioId = targetId)
    }

    fun updateLot(
        positionId: String,
        lotId: String,
        shares: Double,
        costBasisPerShare: Double,
        purchaseDate: String?,
        note: String?,
    ) {
        updatePosition(
            getPositions().first { it.id == positionId }.withUpdatedLot(
                lotId = lotId,
                shares = shares,
                costBasisPerShare = costBasisPerShare,
                purchaseDate = purchaseDate,
                note = note,
            ),
        )
    }

    fun sellLot(positionId: String, lotId: String, sharesToSell: Double) {
        val updated = getPositions().first { it.id == positionId }.withSoldLot(lotId, sharesToSell)
        if (updated.lots.isEmpty()) {
            removePosition(positionId)
        } else {
            updatePosition(updated)
        }
    }

    fun removeLot(positionId: String, lotId: String) {
        val updated = getPositions().first { it.id == positionId }.withoutLot(lotId)
        if (updated.lots.isEmpty()) {
            removePosition(positionId)
        } else {
            updatePosition(updated)
        }
    }

    fun getWatchlist(): List<WatchlistItem> = getActivePortfolio().watchlist

    fun addToWatchlist(symbol: String, note: String? = null) {
        val upper = symbol.trim().uppercase()
        if (upper.isBlank()) return
        val existing = getWatchlist()
        if (existing.any { it.symbol.uppercase() == upper }) return
        updateActive {
            it.copy(
                watchlist = existing + WatchlistItem(
                    symbol = upper,
                    addedDate = null,
                    note = note?.takeIf { text -> text.isNotBlank() },
                ),
            )
        }
    }

    fun removeFromWatchlist(symbol: String) {
        val upper = symbol.uppercase()
        updateActive {
            it.copy(watchlist = it.watchlist.filterNot { item -> item.symbol.uppercase() == upper })
        }
    }

    fun seedSampleDataIfEmpty() {
        if (getPortfolios().any { it.positions.isNotEmpty() }) return
        savePortfolios(
            listOf(
                SavedPortfolio(
                    id = DEFAULT_PORTFOLIO_ID,
                    name = "My Portfolio",
                    colorHex = com.example.positionahead.model.AccountColors.DEFAULT,
                    positions = listOf(
                        positionWithSingleLot("1", "AAPL", 25.0, 178.50, AssetClass.STOCK, "2024-01-15"),
                        positionWithSingleLot("2", "MSFT", 15.0, 380.00, AssetClass.STOCK, "2024-03-01"),
                        positionWithSingleLot("3", "VTI", 40.0, 245.00, AssetClass.ETF, "2024-06-10"),
                        positionWithSingleLot("4", "SCHD", 120.0, 25.80, AssetClass.ETF, "2024-06-10"),
                        positionWithSingleLot("5", "BND", 80.0, 74.20, AssetClass.BOND, "2024-08-01"),
                    ),
                    goal = FinancialGoal(age = 35, riskTolerance = RiskTolerance.MODERATE),
                    watchlist = listOf(
                        WatchlistItem("QQQM", note = "Growth watch"),
                        WatchlistItem("JEPI", note = "Income watch"),
                    ),
                ),
            ),
        )
        setActivePortfolio(DEFAULT_PORTFOLIO_ID)
    }

    private fun updateActive(transform: (SavedPortfolio) -> SavedPortfolio) {
        val activeId = getActivePortfolioId()
        updatePortfolio(activeId, transform)
    }

    private fun updatePortfolio(id: String, transform: (SavedPortfolio) -> SavedPortfolio) {
        savePortfolios(getPortfolios().map { if (it.id == id) transform(it) else it })
    }

    private fun savePortfolios(portfolios: List<SavedPortfolio>) {
        cache = portfolios
        settings[KEY_PORTFOLIOS] = json.encodeToString(portfolios)
    }

    private fun migrateLegacyDataIfNeeded() {
        if (settings.getStringOrNull(KEY_PORTFOLIOS) != null) return
        val legacyPositions = settings.getStringOrNull(KEY_LEGACY_POSITIONS)?.let { raw ->
            runCatching {
                json.decodeFromString<List<StoredPosition>>(raw).map { it.toModel() }
            }.getOrNull()
        }.orEmpty()
        val legacyGoal = settings.getStringOrNull(KEY_LEGACY_GOAL)?.let { raw ->
            runCatching { json.decodeFromString<FinancialGoal>(raw) }.getOrNull()
        } ?: FinancialGoal()
        if (legacyPositions.isNotEmpty() || settings.getStringOrNull(KEY_LEGACY_POSITIONS) != null) {
            savePortfolios(
                listOf(
                    SavedPortfolio(
                        id = DEFAULT_PORTFOLIO_ID,
                        name = "My Portfolio",
                        positions = legacyPositions,
                        goal = legacyGoal,
                    ),
                ),
            )
            setActivePortfolio(DEFAULT_PORTFOLIO_ID)
        }
    }

    private fun defaultPortfolios(): List<SavedPortfolio> = listOf(
        SavedPortfolio(id = DEFAULT_PORTFOLIO_ID, name = "My Portfolio"),
    )

    private fun newLotId(): String = Random.nextLong().toString(16)

    companion object {
        const val DEFAULT_PORTFOLIO_ID = "default"
        private const val KEY_PORTFOLIOS = "portfolios_v2_json"
        private const val KEY_ACTIVE_PORTFOLIO = "active_portfolio_id_v2"
        private const val KEY_LEGACY_POSITIONS = "positions_json"
        private const val KEY_LEGACY_GOAL = "financial_goal_json"
    }
}

@Serializable
private data class StoredPosition(
    val id: String,
    val symbol: String,
    val assetClass: AssetClass = AssetClass.STOCK,
    val lots: List<PositionLot>? = null,
    val shares: Double? = null,
    val costBasisPerShare: Double? = null,
) {
    fun toModel(): Position {
        val resolvedLots = lots?.takeIf { it.isNotEmpty() } ?: listOf(
            PositionLot(
                id = "$id-lot-0",
                shares = shares ?: 0.0,
                costBasisPerShare = costBasisPerShare ?: 0.0,
            ),
        )
        return Position(id = id, symbol = symbol.uppercase(), assetClass = assetClass, lots = resolvedLots)
    }
}
